import { useMemo } from "react";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { useState } from "react";
import MaterialTable from "material-table";
import { PRIMARY_COLOR } from "../../../constants/baseConstants";
import SelectableSearch from "../../base/views/SelectableSearch";
import axiosInstance from "../../../../axiosInstance";
import {
  info,
  success,
  error as showError,
} from "../../../../store/notification/actions";
import { getCatalogSuppliers } from "../helper/reportHelper";
import useAuthUser from "../../base/helper/useAuthUser";
import CustomModal from "../../base/views/CustomModal";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { format } from "date-fns";
import { InfoOutlined } from "@material-ui/icons";
import { exportPICostCsv, exportPICostPdf } from "../helper/piCostReportHelper";

function GeneratePICostReport({ open, selectedDate, onClose, ...props }) {
  const classes = useStyles();
  const { userId, storeId, store } = useAuthUser();
  const [fromDate, setFromDate] = useState(new Date(selectedDate));
  const [toDate, setToDate] = useState(new Date(selectedDate));
  const [selectedSupplier, setSelectedSupplier] = useState({
    id: 0,
    optionname: "",
  });
  const [supplierData, setSupplierData] = useState({ load: false, values: [] });
  const [reportPreview, setReportPreview] = useState([]);

  const handleClose = () => {
    onClose();
    setReportPreview([]);
  };

  const loadingSupplier = () => {
    setSupplierData({ ...supplierData, load: true });
    getCatalogSuppliers(storeId)
      .then((response) => {
        let dropdownData = [];
        if (response.data.length)
          dropdownData = response.data.map((obj) => ({
            id: obj.supplier_id,
            optionname: obj.name,
          }));
        setSupplierData({ load: false, values: dropdownData });
      })
      .catch((err) => {})
      .finally(() => {
        setSupplierData((prev) => ({ ...prev, load: false }));
      });
  };

  async function generateCostReport() {
    try {
      info("Generating report...");
      const result = await axiosInstance.post(
        "/physicalInventory/generateCostReport",
        {
          userId,
          storeId,
          startDate: format(fromDate, "MM-dd-yyyy"),
          endDate: format(toDate, "MM-dd-yyyy"),
          primarySupplierId: selectedSupplier.id,
        }
      );
      success("Report preview generated!");
      setReportPreview(result.data.data);
    } catch (error) {
      showError(
        error.response
          ? error.response.data && error.response.data.message
          : error.message
      );
    }
  }

  async function saveCostReport(stageReportId) {
    try {
      info("Saving report...");
      const result = await axiosInstance.post(
        "/physicalInventory/saveCostReport",
        {
          storeId,
          stageReportId,
        }
      );
      success(
        "Report saved! You may view report from the generated section. Report ID " +
          result?.data?.data?.reportid
      );
      handleClose();
    } catch (error) {
      showError(error.response?.data?.message || error.message);
    }
  }

  const reportTitle = useMemo(() => {
    let title = `Report_Preview_`;
    if (reportPreview.length) {
      title += `${reportPreview[0].reportid}_(${format(
        fromDate,
        "MM-dd-yyyy"
      )}_${format(toDate, "MM-dd-yyyy")})`;
    }
    return title;
  }, [reportPreview]);

  function exportCsvHandler(columns, data) {
    exportPICostCsv(columns, data, reportTitle);
  }

  function exportPdfHandler(columns, data) {
    exportPICostPdf(columns, data, reportTitle, store);
  }

  return (
    <CustomModal
      open={open}
      width={reportPreview.length > 0 ? "80%" : "60%"}
      onClose={handleClose}
      {...props}
    >
      {reportPreview.length > 0 ? (
        <>
          <Grid container style={{ alignItems: "end", padding: 15 }}>
            <Grid item xs={8}>
              <Typography variant="h6">
                PI Cost Report: {format(fromDate, "MM-dd-yyy")} to{" "}
                {format(toDate, "MM-dd-yyyy")}{" "}
              </Typography>
              <FormHelperText>
                This is a preview. Click Save to save the report
              </FormHelperText>
            </Grid>
            <Grid
              item
              style={{
                display: "flex",
                justifyContent: "right",
              }}
              xs={4}
            >
              <Button
                style={{
                  backgroundColor: PRIMARY_COLOR,
                  color: "white",
                  margin: 10,
                }}
                variant="contained"
                onClick={() =>
                  saveCostReport(
                    // selectedInventoryId,
                    reportPreview[0]?.reportid
                  )
                }
              >
                Save
              </Button>
            </Grid>
          </Grid>

          <MaterialTable
            style={{ width: "100%", height: "70vh", overflow: "scroll" }}
            data={reportPreview}
            title={`Report total:
            ${Number(reportPreview[0]?.report_total || 0).toLocaleString(
              "en-us",
              {
                style: "currency",
                currency: "USD",
              }
            )}`}
            columns={[
              { title: "NDC", field: "ndc" },
              { title: "Description", field: "description" },
              { title: "Pack Size", field: "pack_size" },
              { title: "Drug CD", field: "drug_cd" },
              { title: "Form", field: "form" },
              { title: "Gen Status", field: "gen_status" },
              { title: "Inventory Count (Units)", field: "inventory_count" },
              {
                title: "Unit Cost",
                field: "unit_cost",
                render: (row) =>
                  row.unit_cost &&
                  Number(row.unit_cost).toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  }),
              },
              {
                title: "Ext Cost",
                field: "ext_cost",
                render: (row) =>
                  row.ext_cost &&
                  Number(row.ext_cost).toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  }),
              },
            ]}
            options={{
              paging: true,
              pageSize: 25,
              pageSizeOptions: [25, 50, 100, 250, 500],
              actionsColumnIndex: 5,
              exportButton: true,
              exportFileName: reportTitle,
              exportAllData: true,
              exportCsv: exportCsvHandler,
              exportPdf: exportPdfHandler,
            }}
          />
        </>
      ) : (
        <Grid container style={{ padding: 15 }}>
          <Grid item xs={12}>
            <DialogTitle>
              Select dates and catalog to generate a report{" "}
              <Tooltip
                title={`Select the default supplier catalog for product prices. 
                  If the product price is not found in this supplier's catalog,
                  the last modified price irrespective of supplier will be used.`}
              >
                <IconButton
                  style={{
                    height: "1rem",
                    width: "1rem",
                    paddingTop: "0.4rem",
                  }}
                >
                  <InfoOutlined />
                </IconButton>
              </Tooltip>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3} fullWidth>
                <Grid item xs={12} md={6}>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <Grid justify="space-between">
                      <KeyboardDatePicker
                        autoFocus
                        autoOk={true}
                        okLabel={""}
                        cancelLabel={""}
                        margin="normal"
                        id="fromDate"
                        label="From Date"
                        format="MM/dd/yyyy"
                        value={fromDate}
                        fullWidth
                        onChange={(date) => setFromDate(date)}
                        className={classes.submit}
                        KeyboardButtonProps={{
                          "aria-label": "change date",
                        }}
                      />
                    </Grid>
                  </MuiPickersUtilsProvider>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  // style={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <Grid justify="space-around">
                      <KeyboardDatePicker
                        margin="normal"
                        id="toDate"
                        label="To Date"
                        format="MM/dd/yyyy"
                        autoOk={true}
                        okLabel={""}
                        cancelLabel={""}
                        value={toDate}
                        onChange={setToDate}
                        className={classes.submit}
                        fullWidth
                        KeyboardButtonProps={{
                          "aria-label": "change date",
                        }}
                      />
                    </Grid>
                  </MuiPickersUtilsProvider>
                </Grid>

                <Grid item xs={12}>
                  <SelectableSearch
                    value={selectedSupplier}
                    data={supplierData}
                    name="catalog_supplier"
                    label="Primary Supplier"
                    id="supplier"
                    autoFocus={true}
                    loading={loadingSupplier}
                    onChangeEvent={(e, value) => setSelectedSupplier(value)}
                    variantType="standard"
                  ></SelectableSearch>
                </Grid>
              </Grid>
              <DialogActions>
                <Button
                  style={{ backgroundColor: PRIMARY_COLOR, color: "white" }}
                  variant="contained"
                  onClick={() => generateCostReport()}
                >
                  Generate Preview
                </Button>
              </DialogActions>
            </DialogContent>
          </Grid>
        </Grid>
      )}
    </CustomModal>
  );
}

export default GeneratePICostReport;

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiPaper-root": {
      backgroundColor: "rgba(255,255,255,0.5)",
      backdropFilter: "blur(6px)",
      borderRadius: "15px",
      padding: "10px",
      marginTop: "1%",
      justifyContent: "center",
      alignItems: "center",
      widht: "100%",
    },
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: "5px",
    backgroundColor: "rgba(255,255,255,0.5)",
    backdropFilter: "blur(8px)",
  },
}));
